import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Users, CalendarCheck } from "lucide-react";
import useBrand from "../hooks/useBrand";
import { FREE_CALLS } from "../data/content";
import GlobalSearchBar from "./GlobalSearchBar";

const GOOGLE_REVIEWS_URL = "https://share.google/zzVQsYDLpI0cyJmVF";

const GoogleG = (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
    <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
  </svg>
);

const Stars = () => (
  <span className="inline-flex items-center gap-0.5" aria-hidden="true">
    {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={12} fill="#FBBC04" stroke="none" />)}
  </span>
);

const StatChip = ({ icon, value, label, bg, fg }) => (
  <div className="flex items-center gap-2.5 rounded-2xl px-3.5 py-2.5" style={{ background: bg }}>
    <span className="flex-none w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#FFFFFF", color: fg }}>{icon}</span>
    <span className="flex flex-col min-w-0">
      <strong className="font-bricolage text-[20px] leading-none" style={{ color: "#111318" }}>{value}</strong>
      <span className="text-[12.5px] leading-tight mt-0.5 truncate" style={{ color: "#4A505B" }}>{label}</span>
    </span>
  </div>
);

const EXPERTS = [
  { ...FREE_CALLS[0], role: "Avvio, normative, strategia" },
  { ...FREE_CALLS[1], role: "Immagine e home staging" },
  { ...FREE_CALLS[2], role: "Fisco e Partita IVA" },
];

export default function HomeHero() {
  const brand = useBrand();
  return (
    <section className="font-figtree" style={{ background: "#FFFFFF" }} data-testid="home-hero">
      {/* Un solo blocco responsive: una sola <h1> per tutta la pagina */}
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 pt-6 md:pt-8 pb-6 md:pb-8 flex flex-wrap gap-8 md:gap-12 items-center">

        {/* LEFT — contenuto principale */}
        <div className="min-w-0 flex flex-col gap-4 md:gap-6" style={{ flex: "999 1 560px" }}>
          <h1 className="font-bricolage font-extrabold m-0 text-[34px] md:text-[48px] lg:text-[60px]" style={{ lineHeight: 1.04, letterSpacing: "-0.035em", color: "#111318" }} data-testid="home-hero-h1">
            Il portale per Host e PM di <span style={{ color: "#EA580C" }}>affitti brevi, B&amp;B, case vacanza e strutture turistiche</span>
          </h1>

          <p className="m-0 text-[17px] md:text-[20px]" style={{ lineHeight: 1.5, color: "#4A505B", maxWidth: 640 }}>
            Normative aggiornate, tasse, consulenze con professionisti, corsi e strumenti gratuiti per B&amp;B, case vacanza, affittacamere, agriturismo, glamping e locazioni turistiche. Da host, per host.
          </p>

          <div className="w-full" style={{ maxWidth: 640 }} data-testid="home-hero-search">
            <GlobalSearchBar light />
          </div>

          <div className="flex gap-2.5 md:gap-3 flex-wrap items-center">
            <Link to="/servizi" data-testid="home-hero-cta-servizi" className="vdh-btn-yellow h-[56px] md:h-[60px] px-6 md:px-7 rounded-[14px] md:rounded-[16px] flex items-center gap-2.5 font-extrabold text-[17px] md:text-[18px]">
              Scopri i servizi <ArrowRight size={20} />
            </Link>
            <Link to="/consulenze" data-testid="home-hero-cta-consulenze" className="h-[56px] md:h-[60px] px-6 md:px-7 rounded-[14px] md:rounded-[16px] flex items-center font-extrabold text-[17px] md:text-[18px]" style={{ background: "#111318", color: "#FFFFFF", WebkitTextFillColor: "#FFFFFF" }}>
              Prenota una consulenza
            </Link>
            <Link to="/blog" data-testid="home-hero-cta-blog" className="hidden md:flex h-[60px] px-3 items-center font-bold text-[16px] underline underline-offset-4" style={{ color: "#111318" }}>
              Leggi le ultime novità
            </Link>
          </div>

          <div className="flex flex-wrap items-stretch gap-2.5 md:gap-3 pt-4 md:pt-5" style={{ borderTop: "1px solid #ECEEF1" }}>
            {/* Recensioni Google — cliccabile */}
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-hero-google-reviews"
              className="group flex items-center gap-3 rounded-2xl pl-3 pr-4 py-2.5 transition-transform hover:-translate-y-0.5"
              style={{ background: "#FFFFFF", border: "1px solid #E6E8EC", boxShadow: "0 8px 24px -14px rgba(0,0,0,0.3)" }}
              title="Leggi le recensioni su Google"
            >
              <span className="flex-none w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#F6F7F9" }}>{GoogleG}</span>
              <span className="flex flex-col">
                <span className="flex items-center gap-1.5">
                  <strong className="font-bricolage text-[20px] leading-none" style={{ color: "#111318" }}>{brand.recensioni_google_voto}</strong>
                  <Stars />
                </span>
                <span className="text-[12.5px] leading-tight mt-0.5 font-semibold" style={{ color: "#4A505B" }}>
                  {brand.recensioni_google} su Google <span className="group-hover:translate-x-0.5 inline-block transition-transform" style={{ color: "#EA580C" }}>→</span>
                </span>
              </span>
            </a>

            <StatChip icon={<CalendarCheck size={18} />} value="1.000+" label="consulenze svolte" bg="#FFF4E6" fg="#B45309" />
            <StatChip icon={<Users size={18} />} value={`${brand.follower_count}+`} label="follower sui social" bg="#EEF2FF" fg="#4338CA" />
          </div>
        </div>

        {/* RIGHT — card consulenza (desktop) */}
        <aside className="hidden md:flex min-w-0 flex-col gap-4 rounded-[24px] p-7" style={{ flex: "1 1 380px", background: "#111318", color: "#FFFFFF" }} data-testid="home-hero-card">
          <span className="self-start font-extrabold text-[13px] px-3 py-1.5 rounded-full" style={{ background: "#DDF3E6", color: "#0B5E3A" }}>
            GRATIS · 15 MINUTI
          </span>
          <h2 className="font-bricolage text-[30px]" style={{ lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0 }}>
            Parla con un esperto prima di decidere
          </h2>

          {EXPERTS.map((e, i) => (
            <a key={e.key} href={e.bookUrl} target="_blank" rel="noopener noreferrer" data-testid={`home-hero-expert-${e.key}`} className="flex items-center gap-3.5 rounded-[16px] p-3" style={{ background: "#1E2129", color: "#FFFFFF" }}>
              <img src={e.photo} alt={`${e.name}, ${e.role}`} width={48} height={48} loading={i === 0 ? "eager" : "lazy"} className="w-12 h-12 flex-none rounded-2xl object-cover" style={{ background: "#3A3F4B" }} />
              <span className="flex-1 flex flex-col gap-0.5">
                <strong className="text-[16px]">{e.name}</strong>
                <span className="text-[13px]" style={{ color: "#C2C7D0" }}>{e.role}</span>
              </span>
              <span className="font-extrabold text-[14px]" style={{ color: "#FFC233", WebkitTextFillColor: "#FFC233" }}>Prenota</span>
            </a>
          ))}

          <Link to="/consulenze" className="font-bold text-[15px] underline underline-offset-4" style={{ color: "#FFFFFF", WebkitTextFillColor: "#FFFFFF" }}>
            Vedi anche le consulenze approfondite →
          </Link>
        </aside>
      </div>
    </section>
  );
}
